import { getFunctions, httpsCallable } from 'firebase/functions';
// Chuko Logic | Client-downloadable, administrator-approved service report.
// Dependencies: jspdf, firebase/storage, firebase/firestore, firebase/auth.
// Logo: public/service-report-logo.png (the full logo INCLUDING the company name).
// Security: embeds ONLY photos published in the approved service report.
import { getDownloadURL, getStorage, ref as storageRef } from 'firebase/storage';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';

const C = { navy:[20,40,61], ink:[17,17,17], muted:[17,17,17], rule:[20,40,61] };
const LEFT=48, RIGHT=564, WIDTH=RIGHT-LEFT, BOTTOM=712;
const SUBTITLE='Business IT and Network Infrastructure';
const IMAGE_LABELS={before:'Before work',after:'After work',equipment:'Installed equipment',serial:'Serial number',asset_tag:'Asset tag'};
const val=(v)=>typeof v==='string'?v.trim():(v==null?'':String(v));
const pick=(...args)=>args.find(s=>val(s))||'';
const safeFile=s=>val(s).replace(/[^A-Za-z0-9_-]/g,'-').slice(0,60);
const validId=(job)=>{
  const id=val(job.workOrderNumber||job.orderNumber||job.referenceNumber);
  if(!/^B0T\d{5}$/.test(id))throw new Error('A valid B0T service ID is required to download the report. Contact dispatch.');
  return id;
};
function makeDate(value){
  if(!value)return null;
  const d=value?.toDate?.()||(typeof value?.seconds==='number'?new Date(value.seconds*1000):new Date(value));
  return !Number.isNaN(d?.getTime?.())?d:null;
}
function dateText(value){
  const normalized=typeof value==='string' && /^\d{4}-\d{2}-\d{2}$/.test(value)?`${value}T12:00:00`:value;
  const d=makeDate(normalized);
  return d?d.toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}):'Not recorded';
}
// Appointment times are stored as local strings. Display 12-hour time,
// without inventing a timezone conversion or a scheduled end time.
function time12(value){
  const input=val(value);
  const match=/^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?\s*(AM|PM)?$/i.exec(input);
  if(!match)return input||'Not recorded';
  const hours=Number(match[1]);
  const minutes=match[2];
  if(match[3])return `${hours%12||12}:${minutes} ${match[3].toUpperCase()}`;
  return `${hours%12||12}:${minutes} ${hours>=12?'PM':'AM'}`;
}
function addressLines(a){
  if(!a)return [];
  if(typeof a==='string'){
    const parts=a.split(/\r?\n/).map(val).filter(Boolean);
    if(parts.length>1)return parts.slice(0,5);
    return val(a).split(/,\s*/).reduce((rows,part,i)=>{if(i===0)rows.push(part);else if(i===1)rows.push(part);else rows[rows.length-1]+=', '+part;return rows;},[]);
  }
  if(typeof a!=='object'||Array.isArray(a))return [];
  const street=pick(a.street,a.street1,a.address1,a.line1);
  const line2=pick(a.street2,a.address2,a.line2);
  const city=pick(a.city);
  const region=pick(a.state,a.province,a.region);
  const postal=pick(a.postalCode,a.zip);
  const cityLine=[city,[region,postal].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [street,line2,cityLine,pick(a.country)].filter(Boolean).slice(0,5);
}
async function recipientFor(job){
  // The saved work-order snapshot is authoritative for historical reports.
  // A client's profile supplies only missing legacy fields; never fetch strangers' profiles.
  let profile={};
  if(job.clientId){
    try {
      const snapshot=await getDoc(doc(db,'users',job.clientId));
      if(snapshot.exists())profile=snapshot.data();
    } catch { /* Existing stored work-order fields remain usable without profile read. */ }
  }
  const company=pick(job.clientCompany,job.businessName,job.companyName,job.clientDetails?.companyName,profile.company,profile.companyName)||'Company not recorded';
  const addr=pick(job.clientBusinessAddress,job.businessAddress,job.companyAddress,job.clientDetails?.businessAddress,profile.businessAddress);
  const poc=pick(job.clientName,job.contactName,job.clientDetails?.contactName,profile.contactName,profile.name);
  const phone=pick(job.clientPhone,job.contactPhone,job.clientDetails?.phone,profile.phone);
  const email=pick(job.clientEmail,job.contactEmail,job.clientDetails?.email,profile.email);
  return {company,addresses:addressLines(addr),poc,phone,email};
}
async function fetchBlob(url){
  const response=await fetch(url,{cache:'no-store'});
  if(!response.ok)throw new Error(`Unable to retrieve image (HTTP ${response.status}).`);
  return response.blob();
}
async function toJpeg(blob,maxEdge=1500){
  const url=URL.createObjectURL(blob);
  try{
    const img=new Image();
    await new Promise((resolve,reject)=>{img.onload=resolve;img.onerror=()=>reject(new Error('Unreadable report image.'));img.src=url;});
    const scale=Math.min(1,maxEdge/Math.max(img.naturalWidth,img.naturalHeight));
    const canvas=document.createElement('canvas');
    canvas.width=Math.max(1,Math.round(img.naturalWidth*scale));
    canvas.height=Math.max(1,Math.round(img.naturalHeight*scale));
    const ctx=canvas.getContext('2d');
    if(!ctx)throw new Error('Unable to process report image.');
    ctx.fillStyle='#ffffff';ctx.fillRect(0,0,canvas.width,canvas.height);
    ctx.drawImage(img,0,0,canvas.width,canvas.height);
    return {data:canvas.toDataURL('image/jpeg',0.86),width:canvas.width,height:canvas.height};
  }finally{URL.revokeObjectURL(url);}
}

// Authenticated backend transport: a normal <img> tag may display a Storage URL
// while fetch() cannot read its bytes because of browser cross-origin policy.
// The callable checks the signed-in user AND the photo's membership in the
// admin-approved report. No public Storage URLs or rules changes are needed.
async function loadApprovedPhotoForPdf(jobId, photoPath) {
  const response = await httpsCallable(getFunctions(), 'getApprovedReportPhoto')({
    jobId,
    path: photoPath,
  });
  const { base64, contentType } = response?.data || {};
  if (typeof base64 !== 'string' || !base64.length ||
      !['image/jpeg','image/png','image/webp'].includes(contentType)) {
    throw new Error('The approved photo response was not a supported image.');
  }
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i=0; i<raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return new Blob([bytes], { type: contentType });
}

export async function downloadServiceReportPdf(job){
  if(!job||job.serviceReportStatus!=='approved'||!job.serviceReport?.approvedAt){
    throw new Error('An approved service report is required for PDF download.');
  }
  const [{jsPDF},recipient]=await Promise.all([import('jspdf'),recipientFor(job)]);
  const pdf=new jsPDF({unit:'pt',format:'letter',compress:true});
  const id=validId(job),report=job.serviceReport;
  let logo;
  try {logo=await toJpeg(await fetchBlob('/service-report-logo.png'),1400);}
  catch {throw new Error('Place your approved wordmark at public/service-report-logo.png.');}
  // This is the *admin-approved snapshot*, never unpublished Storage files.
  // Missing photo references cannot be reconstructed by the PDF frontend.
  const snapshots=Array.isArray(report.photos)?report.photos:[];
  const approved=snapshots.filter(p=>p&&typeof p.path==='string'&&p.path.trim()&&IMAGE_LABELS[p.category]);
  if(approved.length!==snapshots.length){
    throw new Error('The approved report contains malformed photo references. Ask Chuko Logic to correct the approved report.');
  }
  if(job.evidencePolicyVersion===1){
    const categories=new Set(approved.map(p=>p.category));
    const required=['before','after'];
    if(report.equipmentInstalled){
      required.push('equipment','serial');
      if(!report.assetTagNotPresent)required.push('asset_tag');
    }
    if(required.some(category=>!categories.has(category))){
      throw new Error('Approved service report has missing required photos. Ask Chuko Logic to review the report evidence before downloading.');
    }
  }
  let y=264;
  const ink=()=>pdf.setTextColor(...C.ink);
  const font=(size=10,bold=false)=>{ink();pdf.setFont('helvetica',bold?'bold':'normal');pdf.setFontSize(size);};
  const pageHeader=(first=false)=>{
    const ratio=Math.min(242/logo.width,49/logo.height);
    pdf.addImage(logo.data,'JPEG',LEFT,26,logo.width*ratio,logo.height*ratio);
    font(9,true);pdf.text(SUBTITLE,LEFT,95);
    font(8.5);pdf.text('chukologic.com',RIGHT,48,{align:'right'});
    pdf.text('dispatch@chukologic.com',RIGHT,61,{align:'right'});
    pdf.text('(920) 724-1438',RIGHT,74,{align:'right'});
    if(first){
      font(16,true);pdf.text('SERVICE COMPLETION REPORT',LEFT,132);
      // One shared metadata band: Service ID/title left, recipient details right.
      font(11,true);pdf.text(`Service ID ${id}`,LEFT,162);
      font(10,false);
      const service=pdf.splitTextToSize(pick(job.serviceType,job.title)||'IT service',244);
      let serviceY=182;
      for(const row of service.slice(0,3)){pdf.text(row,LEFT,serviceY);serviceY+=14;}

      // The Prepared For heading matches the Service ID type size and is
      // RIGHT-ALIGNED to the page margin (not placed near the center).
      font(11,true);pdf.text('PREPARED FOR',RIGHT,162,{align:'right'});
      const recipientLines=[recipient.company,...(recipient.addresses.length?recipient.addresses:['Business address not recorded'])];
      if(recipient.poc)recipientLines.push(`Attn: ${recipient.poc} (POC)`);
      if(recipient.phone)recipientLines.push(recipient.phone);
      if(recipient.email)recipientLines.push(recipient.email);
      let recipientY=182;
      recipientLines.forEach((line,i)=>{
        font(i===0?10:9,i===0);
        // Wrap to the right-column width, then align EACH line to the right edge.
        for(const row of pdf.splitTextToSize(line,235).slice(0,2)){
          pdf.text(row,RIGHT,recipientY,{align:'right'});
          recipientY+=12.5;
        }
      });
      // First divider: immediately below the entire two-column header.
      const separatorY=Math.max(240,recipientY+14,serviceY+14);
      pdf.setDrawColor(...C.rule);pdf.setLineWidth(0.8);
      pdf.line(LEFT,separatorY,RIGHT,separatorY);
      y=separatorY+27;
    }
  };
  const nextPage=()=>{pdf.addPage();pageHeader();y=118;};
  const space=need=>{if(y+need>BOTTOM)nextPage();};
  const write=(s,{size=10,bold=false,indent=0,after=0}={})=>{
    font(size,bold);
    const lines=pdf.splitTextToSize(val(s)||'Not recorded',WIDTH-indent);
    const lineHeight=size+5;
    for(const row of lines){space(lineHeight);font(size,bold);pdf.text(row,LEFT+indent,y);y+=lineHeight;}
    y+=after;
  };
  const section=(label,text)=>{
    space(65);y+=15;font(10,true);pdf.text(label.toUpperCase(),LEFT,y);y+=19;
    write(text,{size:10,after:4});
  };
  pageHeader(true);
  // No technician check-in/check-out data in the customer report.
  // Only the scheduled service start is shown, using 12-hour time.
  if(job.schedule?.date&&job.schedule.startTime){
    section('Scheduled service',`${dateText(job.schedule.date)} at ${time12(job.schedule.startTime)}`);
  }
  section('Findings and diagnosis',report.diagnosis);
  section('Work performed',report.workPerformed);
  section('Testing and verification',report.testsPerformed);
  section('Equipment and asset information',report.equipment||'None documented');
  if(report.equipmentInstalled&&report.assetTagNotPresent)section('Asset tag exception',report.assetTagNote||'Not recorded');
  // Second (and only body) divider: immediately before Recommendations.
  space(85);
  y+=12;
  pdf.setDrawColor(...C.rule);pdf.setLineWidth(0.7);
  pdf.line(LEFT,y,RIGHT,y);
  y+=11;
  section('Recommendations',report.recommendations||'None');
  space(54);y+=16;font(10,true);pdf.text('PHOTOGRAPHIC EVIDENCE',LEFT,y);y+=19;
  if(!approved.length){
    write('No approved photographs were attached when this report was approved.',{size:10,after:8});
  }else{
    for(let i=0;i<approved.length;i++){
      const p=approved[i];let img;
      try{
        img=await toJpeg(await loadApprovedPhotoForPdf(job.id,p.path));
      }catch{throw new Error(`Could not load approved ${IMAGE_LABELS[p.category].toLowerCase()} photograph ${i+1}. No partial report was saved.`);}
      const factor=Math.min(WIDTH/img.width,266/img.height);
      const width=img.width*factor,height=img.height*factor;
      space(height+72);
      write(`${i+1}. ${IMAGE_LABELS[p.category]}`,{size:10,bold:true,after:5});
      pdf.addImage(img.data,'JPEG',LEFT,y,width,height);y+=height+12;
      if(p.caption)write(p.caption,{size:9,after:6});
      y+=12;
    }
  }
  space(55);y+=17;
  write(`Reviewed and approved by Chuko Logic on ${dateText(report.approvedAt)}.`,{size:9,after:5});
  write('Additional work requires a separately authorized quote.',{size:9});
  const pages=pdf.getNumberOfPages();
  for(let n=1;n<=pages;n++){
    pdf.setPage(n);font(8);pdf.text(`Service ID ${id}`,LEFT,758);
    pdf.text(`Page ${n} of ${pages}`,RIGHT,758,{align:'right'});
  }
  pdf.setProperties({title:`Service ID ${id} | Chuko Logic`,subject:'Approved service completion report',author:'Chuko Logic'});
  pdf.save(`Chuko-Logic-Service-${safeFile(id)}.pdf`);
}
